<div class="viewer-wrap">

  <!-- Header -->
  <header class="viewer-header">
    <img src="/favicon.ico" alt="Treantly" width="28" height="28" style="flex-shrink:0;margin-right:4px;" />
    <span class="brand-name fw-bold">Treantly</span>
    <span class="brand-sub">Partner Portal</span>
  </header>

  <!-- Loading -->
  @if (loading) {
    <div class="state-box">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-3 text-muted">Loading timesheets…</p>
    </div>
  }

  <!-- Invalid link -->
  @if (!loading && invalid) {
    <div class="state-box">
      <span class="material-icons state-icon" style="color:#dc3545">link_off</span>
      <p class="mt-2 fw-semibold">This link is invalid or has been deactivated.</p>
      <p class="text-muted small">Please contact Treantly if you believe this is an error.</p>
    </div>
  }

  <!-- Content -->
  @if (!loading && data) {
    <div class="viewer-layout">

      <!-- Sidebar -->
      <aside class="va-sidebar">
        <div class="sidebar-label">Treantly Assistants</div>

        <button
          class="va-sidebar-item"
          [class.active]="!selectedEmp"
          (click)="selectEmployee(null)"
        >
          <span class="material-icons va-sidebar-icon">groups</span>
          <div class="va-sidebar-text">
            <span class="va-sidebar-name">All TAs</span>
            <span class="va-sidebar-meta">{{ data.employees.length }} assigned</span>
          </div>
        </button>

        @for (emp of data.employees; track emp.employee_id) {
          <button
            class="va-sidebar-item"
            [class.active]="selectedEmp?.employee_id === emp.employee_id"
            (click)="selectEmployee(emp)"
          >
            <span class="material-icons va-sidebar-icon">account_circle</span>
            <div class="va-sidebar-text">
              <span class="va-sidebar-name">{{ emp.name }}</span>
              <span class="va-sidebar-meta">{{ emp.role }} &middot; {{ emp.pto_balance }} PTO left</span>
            </div>
            @if (pendingCount(emp) > 0) {
              <span class="pending-badge">{{ pendingCount(emp) }}</span>
            }
          </button>
        }

        <div class="va-sidebar-footer">
          <button
            class="va-sidebar-item"
            [class.active]="activeTab === 'manual'"
            (click)="activeTab = 'manual'"
          >
            <span class="material-icons va-sidebar-icon">menu_book</span>
            <div class="va-sidebar-text">
              <span class="va-sidebar-name">Manual</span>
            </div>
          </button>
        </div>
      </aside>

      <!-- Main panel -->
      <main class="viewer-main">

        <!-- Greeting + period filter -->
        <div class="greeting-bar mb-4">
          <div>
            <div class="greeting-title">Hello, {{ data.approver.first_name }}</div>
            <div class="greeting-sub">
              @if (selectedEmp) {
                Showing timesheets for <strong>{{ selectedEmp.name }}</strong>
                &middot; <strong>{{ selectedEmp.pto_balance }}</strong> PTO day{{ selectedEmp.pto_balance === 1 ? '' : 's' }} remaining
              } @else {
                All timesheets for client <strong>{{ data.client_code }}</strong>
              }
            </div>
          </div>
          @if (yearOptions.length > 0) {
            <div class="period-filter-bar">
              <label class="period-filter-label">Year</label>
              <select class="form-select form-select-sm" style="width:auto" [(ngModel)]="selectedYear" (ngModelChange)="onYearChange()">
                <option [ngValue]="null">All</option>
                @for (y of yearOptions; track y) {
                  <option [ngValue]="y">{{ y }}</option>
                }
              </select>

              <label class="period-filter-label">Month</label>
              <select class="form-select form-select-sm" style="width:auto" [(ngModel)]="selectedMonth">
                <option [ngValue]="null">All</option>
                @for (m of monthOptions; track m.value) {
                  <option [ngValue]="m.value">{{ m.label }}</option>
                }
              </select>

              <label class="period-filter-label">Period</label>
              <select class="form-select form-select-sm" style="width:auto" [(ngModel)]="selectedHalf">
                <option [ngValue]="null">All</option>
                @for (h of halfOptions; track h.value) {
                  <option [value]="h.value">{{ h.label }}</option>
                }
              </select>
            </div>
          }
        </div>

        <!-- Section tabs -->
        <div class="portal-tabs mb-4">
          <button class="portal-tab" [class.active]="activeTab === 'timesheets'" (click)="activeTab = 'timesheets'">
            <span class="material-icons">schedule</span> Timesheets
            @if (pendingTotal > 0) { <span class="portal-tab-badge">{{ pendingTotal }}</span> }
          </button>
          <button class="portal-tab" [class.active]="activeTab === 'leave-requests'" (click)="activeTab = 'leave-requests'">
            <span class="material-icons">beach_access</span> Leave Requests
            @if (pendingLeaveTotal > 0) { <span class="portal-tab-badge">{{ pendingLeaveTotal }}</span> }
          </button>
        </div>

        @if (activeTab === 'timesheets') {

        <!-- Summary banner -->
        @if (data.employees.length > 0) {
          <div class="summary-banner mb-4" [class.summary-banner-done]="pendingTotal === 0">
            <span class="material-icons summary-icon">
              {{ pendingTotal > 0 ? 'pending_actions' : 'check_circle' }}
            </span>
            @if (pendingTotal > 0) {
              <span>
                <strong>{{ pendingTotal }} timesheet{{ pendingTotal > 1 ? 's' : '' }}</strong>
                awaiting your review — timesheets are auto-approved after 5 days if no action is taken.
              </span>
            } @else {
              <span>All timesheets have been reviewed.</span>
            }
          </div>
        }

        <!-- No VAs assigned -->
        @if (data.employees.length === 0) {
          <div class="state-box">
            <span class="material-icons state-icon text-muted">group_off</span>
            <p class="mt-2 text-muted">No virtual assistants are assigned to you yet.</p>
          </div>
        }

        <!-- VA cards -->
        @for (emp of displayedEmployees; track emp.employee_id) {
          <div class="va-card mb-3">

            <!-- Excel-style timesheet sections (no separate card header — name is in xl-header) -->
            <div class="xl-sections-wrap">
              @for (ts of filteredTimesheets(emp); track ts.id) {
                <div class="xl-section">

                  <!-- Single-line header: name + period + hours + status + actions -->
                  <div class="xl-header" (click)="toggleTsExpand(ts.id)">

                    <!-- Title: name and/or period depending on active filters -->
                    @if (!selectedEmp && !isPeriodFiltered) {
                      <span class="xl-header-title">
                        <span class="xl-emp-name">{{ emp.name }}</span>
                        <span class="xl-title-sep">—</span>
                        <span class="xl-period-label">{{ periodLabel(ts) }}</span>
                      </span>
                    } @else if (!selectedEmp && isPeriodFiltered) {
                      <span class="xl-header-title">{{ emp.name }}</span>
                    } @else if (selectedEmp && !isPeriodFiltered) {
                      <span class="xl-header-title">{{ periodLabel(ts) }}</span>
                    } @else {
                      <span class="xl-header-title">{{ emp.name }}</span>
                    }

                    <!-- Total hours + OT -->
                    <span class="xl-hours-pill">
                      <span class="material-icons" style="font-size:0.8rem;vertical-align:middle">schedule</span>
                      {{ +ts.total_hours | number:'1.2-2' }} hrs
                      @if (periodOvertimeHours(ts) > 0) {
                        <span class="xl-ot-tag">+{{ periodOvertimeHours(ts) | number:'1.2-2' }} OT</span>
                      }
                    </span>

                    <!-- On-call stipend flag — staffing context only, no dollar
                         amount shown to the client. The stipend itself is
                         capped at 1 per calendar month regardless of how many
                         on-call rotations were logged (see
                         PayrollResultController), so this never shows a
                         count/multiplier — an "Nx" tag here would read as
                         "billed N times," which isn't what happens. -->
                    @if (ts.oncall_stipend_stint_count > 0) {
                      <span class="xl-oncall-pill" title="Stipend applies this period">
                        <span class="material-icons" style="font-size:0.8rem;vertical-align:middle">phone_in_talk</span>
                        Stipend
                      </span>
                    }

                    <!-- Separate from the stipend flag above -- on-call hours
                         get logged (and shown to the client) whether or not
                         this employee has a stipend arrangement at all, same
                         as the day-by-day On-Call Hours table in the
                         expanded view. Hours only, no dollar amount. -->
                    @if (ts.oncall_entries.length > 0) {
                      <span class="xl-oncall-pill" title="On-call hours logged this period">
                        <span class="material-icons" style="font-size:0.8rem;vertical-align:middle">phone_in_talk</span>
                        On-call: {{ getOncallTotalHours(ts) | number:'1.2-2' }} hrs
                      </span>
                    }

                    <!-- Status badge -->
                    @if (ts.status === 'client_approved' || ts.status === 'approved') {
                      <span class="xl-completed-badge">&#10003; PAYROLL COMPLETED</span>
                    } @else {
                      <span class="ts-badge" [class]="'status-' + statusKey(ts)" style="font-size:0.68rem">
                        {{ statusLabel(ts) }}
                      </span>
                    }

                    <!-- Action buttons -->
                    <div class="xl-header-actions" (click)="$event.stopPropagation()">
                      <button class="btn-sm btn-download" title="Download this timesheet as a PDF"
                        (click)="downloadTimesheetPdf(ts, emp)">
                        <span class="material-icons btn-icon">download</span> Download
                      </button>
                      @if (canApprove(ts)) {
                        <button class="btn-sm btn-approve"
                          [disabled]="actionPending === ts.id"
                          (click)="approve(ts)">
                          @if (actionPending === ts.id) {
                            <span class="spinner-border spinner-border-sm"></span>
                          } @else {
                            <span class="material-icons btn-icon">check_circle</span>
                          }
                          Approve
                        </button>
                      }
                      @if (canReject(ts)) {
                        <button class="btn-sm btn-flag"
                          [disabled]="actionPending === ts.id"
                          (click)="openReject(ts)">
                          <span class="material-icons btn-icon">flag</span> Flag
                        </button>
                      } @else if (ts.payroll_finalized && ts.status !== 'submitted') {
                        <span class="text-muted small fst-italic">Payroll finalized — contact your account manager to report a discrepancy</span>
                      }
                    </div>

                    <span class="xl-chevron">{{ expandedTsIds.has(ts.id) ? '▲' : '▼' }}</span>
                  </div>

                  <!-- Expanded: daily log table -->
                  @if (expandedTsIds.has(ts.id)) {
                    <div class="xl-body">

                      <!-- Hours summary strip -->
                      <div class="xl-hours-strip">
                        <div class="xl-hours-stat">
                          <span class="xl-hours-stat-label">Total Hours Worked</span>
                          <span class="xl-hours-stat-value">{{ +ts.total_hours | number:'1.2-2' }}</span>
                        </div>
                        @if (ts.agreed_hours_weekly) {
                          <div class="xl-hours-stat">
                            <span class="xl-hours-stat-label">Expected Hours</span>
                            <span class="xl-hours-stat-value">{{ periodExpectedHours(ts) | number:'1.2-2' }}</span>
                          </div>
                          <div class="xl-hours-stat" [class.xl-hours-stat-ot]="periodOvertimeHours(ts) > 0">
                            <span class="xl-hours-stat-label">Overtime</span>
                            <span class="xl-hours-stat-value">
                              @if (periodOvertimeHours(ts) > 0) {
                                +{{ periodOvertimeHours(ts) | number:'1.2-2' }} hrs
                              } @else {
                                None
                              }
                            </span>
                          </div>
                          <div class="xl-hours-stat">
                            <span class="xl-hours-stat-label">Agreed (weekly)</span>
                            <span class="xl-hours-stat-value">{{ ts.agreed_hours_weekly }} hrs/wk</span>
                          </div>
                        }
                      </div>

                      <table class="xl-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th class="text-center">Time In</th>
                            <th class="text-center">Time Out</th>
                            <th>Type</th>
                            <th class="text-center">Total No. of Hours Worked</th>
                            <th>Remarks</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (log of ts.logs; track log.id) {
                            <tr
                              [class.row-day-off]="log.leave_type === 'day_off'"
                              [class.row-leave]="log.leave_type && log.leave_type !== 'day_off'"
                              [class.row-break]="log.is_break"
                            >
                              <td class="xl-cell-date">{{ log.date | date:'EEEE, MMMM d' }}</td>
                              <td class="text-center">{{ time12(log.time_in) }}</td>
                              <td class="text-center">{{ time12(log.time_out) }}</td>
                              <td>{{ log.is_break ? 'Break' : leaveLabel(log.leave_type) }}</td>
                              <td class="text-center fw-semibold">
                                {{ +log.total_hours === 0 ? '—' : (+log.total_hours | number:'1.2-2') }}
                                @if (breakDeductedHours(log) > 0) {
                                  <div class="text-muted small fw-normal"
                                       [title]="'Raw span ' + time12(log.time_in) + '–' + time12(log.time_out) + ' minus ' + (breakDeductedHours(log) | number:'1.2-2') + ' hr break'">
                                    −{{ breakDeductedHours(log) | number:'1.2-2' }} hr break
                                  </div>
                                }
                              </td>
                              <td class="text-muted small">{{ log.remarks || '' }}</td>
                            </tr>
                          } @empty {
                            <tr>
                              <td colspan="6" class="text-center text-muted py-3">No daily records for this period.</td>
                            </tr>
                          }
                          <tr class="xl-total-row">
                            <td colspan="4" class="text-end pe-3">Total No. of Hours Worked:</td>
                            <td class="text-center fw-semibold">{{ +ts.total_hours | number:'1.2-2' }}</td>
                            <td></td>
                          </tr>
                          @if (ts.client_comment) {
                            <tr class="xl-footer-row">
                              <td>Client Comment:</td>
                              <td colspan="5">{{ ts.client_comment }}</td>
                            </tr>
                          }
                          @if (ts.client_rejection_reason) {
                            <tr class="xl-footer-row xl-footer-flag">
                              <td>Flag Reason:</td>
                              <td colspan="5">{{ ts.client_rejection_reason }}</td>
                            </tr>
                          }
                          @if (ts.approver_actions?.length) {
                            <tr class="xl-footer-row">
                              <td>Approver History:</td>
                              <td colspan="5">
                                @for (act of ts.approver_actions; track act.id) {
                                  <div style="font-size:0.78rem">
                                    <span class="material-icons"
                                      style="font-size:0.85rem;vertical-align:middle;margin-right:2px"
                                      [style.color]="act.action === 'rejected' ? '#842029' : '#0f5132'">
                                      {{ act.action === 'rejected' ? 'cancel' : 'check_circle' }}
                                    </span>
                                    <strong>{{ act.approver_name }}</strong>
                                    {{ act.action === 'rejected' ? 'flagged' : 'approved' }}
                                    @if (act.reason) { — "{{ act.reason }}" }
                                  </div>
                                }
                              </td>
                            </tr>
                          }
                        </tbody>
                      </table>

                      @if (ts.oncall_entries.length) {
                        <table class="xl-table" style="margin-top:0.75rem">
                          <thead>
                            <tr>
                              <th colspan="4" style="font-size:0.7rem;text-transform:uppercase;letter-spacing:0.06em;color:#6c757d;background:transparent;border:none;padding-bottom:0.25rem">On-Call Hours</th>
                            </tr>
                            <tr>
                              <th>Date</th>
                              <th class="text-center">Time</th>
                              <th class="text-center">Hours</th>
                              <th>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            @for (entry of ts.oncall_entries; track entry.id) {
                              <tr>
                                <td class="xl-cell-date">{{ oncallDateLabel(entry) }}</td>
                                <td class="text-center">{{ oncallTimeLabel(entry) }}</td>
                                <td class="text-center fw-semibold">{{ +entry.hours | number:'1.2-2' }}</td>
                                <td class="text-muted small">{{ entry.notes || '' }}</td>
                              </tr>
                            }
                            <tr class="xl-total-row">
                              <td colspan="2" class="text-end pe-3">Total On-Call Hours:</td>
                              <td class="text-center fw-semibold">{{ getOncallTotalHours(ts) | number:'1.2-2' }}</td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      }
                    </div>
                  }

                </div>
              } @empty {
                <div class="ts-empty">No timesheets submitted for this period.</div>
              }
            </div>

          </div>
        }

        } <!-- /activeTab === 'timesheets' -->

        @if (activeTab === 'leave-requests') {

          @if (displayedLeaveRequests.length > 0) {
            <div class="summary-banner mb-4" [class.summary-banner-done]="pendingLeaveTotal === 0">
              <span class="material-icons summary-icon">
                {{ pendingLeaveTotal > 0 ? 'pending_actions' : 'check_circle' }}
              </span>
              @if (pendingLeaveTotal > 0) {
                <span>
                  <strong>{{ pendingLeaveTotal }} leave request{{ pendingLeaveTotal > 1 ? 's' : '' }}</strong>
                  awaiting your review.
                </span>
              } @else {
                <span>All leave requests have been reviewed.</span>
              }
            </div>
          }

          @if (displayedLeaveRequests.length === 0) {
            <div class="state-box">
              <span class="material-icons state-icon text-muted">event_busy</span>
              <p class="mt-2 text-muted">No leave requests to show.</p>
            </div>
          } @else {
            <div class="va-card mb-3">
              <div class="xl-sections-wrap">
                @for (r of displayedLeaveRequests; track r.id) {
                  <div class="xl-section">
                    <div class="xl-header" style="cursor:default">
                      <span class="xl-header-title">
                        <span class="xl-emp-name">{{ employeeName(r.employee_id) }}</span>
                        <span class="xl-title-sep">—</span>
                        <span class="xl-period-label">{{ leaveTypeLabel(r.leave_type) }} · {{ leavePortionLabel(r.day_portion) }}</span>
                      </span>

                      <span class="xl-hours-pill">
                        <span class="material-icons" style="font-size:0.8rem;vertical-align:middle">event</span>
                        {{ leaveDateRange(r) }} ({{ leaveDays(r) }} {{ leaveDays(r) === 1 ? 'day' : 'days' }})
                      </span>

                      <span class="ts-badge" [class]="'status-' + r.status" style="font-size:0.68rem;text-transform:capitalize">
                        {{ r.status }}
                      </span>

                      <div class="xl-header-actions">
                        @if (r.status === 'pending') {
                          <button class="btn-sm btn-approve"
                            [disabled]="leaveActionPending === r.id"
                            (click)="approveLeave(r)">
                            @if (leaveActionPending === r.id) {
                              <span class="spinner-border spinner-border-sm"></span>
                            } @else {
                              <span class="material-icons btn-icon">check_circle</span>
                            }
                            Approve
                          </button>
                          <button class="btn-sm btn-flag"
                            [disabled]="leaveActionPending === r.id"
                            (click)="openRejectLeave(r)">
                            <span class="material-icons btn-icon">close</span> Decline
                          </button>
                        } @else if (r.status === 'approved') {
                          <span class="text-muted small fst-italic me-2">
                            Approved by {{ r.approved_by_type === 'hr' ? 'HR' : (r.approved_by || 'client') }}
                          </span>
                          <button class="btn-sm btn-flag"
                            [disabled]="leaveActionPending === r.id"
                            (click)="openRejectLeave(r)"
                            title="Reverse this approval and refund the reserved days">
                            <span class="material-icons btn-icon">undo</span> Rescind
                          </button>
                        } @else if (r.status === 'rejected') {
                          <span class="text-muted small fst-italic">
                            Declined by {{ r.rejected_by_type === 'hr' ? 'HR' : (r.rejected_by || 'client') }}
                          </span>
                        }
                      </div>
                    </div>

                    @if (r.reason || (r.status === 'rejected' && r.rejection_reason)) {
                      <div class="xl-body">
                        @if (r.reason) {
                          <div class="ts-detail-note">
                            <span class="fw-semibold">Reason for leave:</span> {{ r.reason }}
                          </div>
                        }
                        @if (r.status === 'rejected' && r.rejection_reason) {
                          <div class="ts-detail-note" style="border-color:#f5c2c7;color:#842029;background:#f8d7da">
                            <span class="fw-semibold">Decline reason:</span> {{ r.rejection_reason }}
                          </div>
                        }
                      </div>
                    }
                  </div>
                } @empty {
                  <div class="ts-empty">No leave requests found.</div>
                }
              </div>
            </div>
          }

        }

        @if (activeTab === 'manual') {
          <app-manual />
        }

      </main>
    </div>
  }

</div>

<!-- Timesheet detail modal -->
@if (viewingTimesheet && viewingEmployee) {
  <div class="modal-backdrop-custom" (click)="closeView()"></div>
  <div class="modal-panel modal-panel-wide" (click)="$event.stopPropagation()">

    <div class="modal-panel-header">
      <div>
        <div style="font-size:1rem;font-weight:700">{{ periodLabel(viewingTimesheet) }}</div>
        <div style="font-size:0.8rem;color:#6c757d;margin-top:2px">
          {{ viewingEmployee.name }} &bull; {{ viewingEmployee.role }}
        </div>
      </div>
      <span class="ts-badge ms-3" [class]="'status-' + statusKey(viewingTimesheet)">
        {{ statusLabel(viewingTimesheet) }}
      </span>
      <button class="modal-close ms-auto" (click)="closeView()">
        <span class="material-icons">close</span>
      </button>
    </div>

    <!-- Summary stats -->
    <div class="ts-detail-stats">
      <div class="ts-detail-stat">
        <div class="ts-detail-stat-label">Total Hours</div>
        <div class="ts-detail-stat-value">{{ viewingTimesheet.total_hours }}</div>
      </div>
      @if (viewingTimesheet.agreed_hours_weekly) {
        <div class="ts-detail-stat">
          <div class="ts-detail-stat-label">Agreed / Week</div>
          <div class="ts-detail-stat-value">{{ viewingTimesheet.agreed_hours_weekly }} hrs</div>
        </div>
      }
      @if (viewingTimesheet.oncall_stipend_stint_count > 0) {
        <div class="ts-detail-stat">
          <div class="ts-detail-stat-label">On-Call Stipend</div>
          <!-- Capped at 1/month regardless of rotation count -- no "(Nx)"
               here for the same reason as the summary pill above. -->
          <div class="ts-detail-stat-value">Applies</div>
        </div>
      }
      @if (viewingTimesheet.submitted_at) {
        <div class="ts-detail-stat">
          <div class="ts-detail-stat-label">Submitted</div>
          <div class="ts-detail-stat-value">{{ viewingTimesheet.submitted_at | date:'MMM d, y' }}</div>
        </div>
      }
      @if (viewingTimesheet.client_approved_at) {
        <div class="ts-detail-stat">
          <div class="ts-detail-stat-label">
            {{ isAutoApproved(viewingTimesheet) ? 'Auto-Approved' : 'Approved' }}
          </div>
          <div class="ts-detail-stat-value">{{ viewingTimesheet.client_approved_at | date:'MMM d, y' }}</div>
        </div>
      }
    </div>

    <!-- Daily log table -->
    <div class="ts-detail-table-wrap">
      <table class="ts-detail-table">
        <thead>
          <tr>
            <th>Date</th>
            <th class="text-center">Time In</th>
            <th class="text-center">Time Out</th>
            <th>Type</th>
            <th class="text-center">Hours</th>
            <th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          @for (log of viewingTimesheet.logs; track log.id) {
            <tr
              [class.row-day-off]="log.leave_type === 'day_off'"
              [class.row-leave]="log.leave_type && log.leave_type !== 'day_off'"
              [class.row-break]="log.is_break"
            >
              <td class="cell-date">{{ log.date | date:'EEE, MMM d' }}</td>
              <td class="text-center">{{ time12(log.time_in) }}</td>
              <td class="text-center">{{ time12(log.time_out) }}</td>
              <td>{{ log.is_break ? 'Break' : leaveLabel(log.leave_type) }}</td>
              <td class="text-center fw-semibold">
                {{ +log.total_hours === 0 ? '—' : (+log.total_hours | number:'1.2-2') }}
                @if (breakDeductedHours(log) > 0) {
                  <div class="text-muted small fw-normal"
                       [title]="'Raw span ' + time12(log.time_in) + '–' + time12(log.time_out) + ' minus ' + (breakDeductedHours(log) | number:'1.2-2') + ' hr break'">
                    −{{ breakDeductedHours(log) | number:'1.2-2' }} hr break
                  </div>
                }
              </td>
              <td class="text-muted small">{{ log.remarks || '' }}</td>
            </tr>
          } @empty {
            <tr><td colspan="6" class="text-center text-muted py-3">No daily records for this period.</td></tr>
          }
          <tr class="row-total">
            <td colspan="4" class="text-end fw-bold small pe-3">Total Hours Worked:</td>
            <td class="text-center fw-bold">{{ +viewingTimesheet.total_hours | number:'1.2-2' }}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>

    @if (viewingTimesheet.oncall_entries.length) {
      <div class="ts-detail-table-wrap" style="margin-top:1rem">
        <div class="fw-semibold mb-2" style="font-size:0.75rem;text-transform:uppercase;letter-spacing:0.06em;color:#6c757d">On-Call Hours</div>
        <table class="ts-detail-table">
          <thead>
            <tr>
              <th>Date</th>
              <th class="text-center">Time</th>
              <th class="text-center">Hours</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            @for (entry of viewingTimesheet.oncall_entries; track entry.id) {
              <tr>
                <td class="cell-date">{{ oncallDateLabel(entry) }}</td>
                <td class="text-center">{{ oncallTimeLabel(entry) }}</td>
                <td class="text-center fw-semibold">{{ +entry.hours | number:'1.2-2' }}</td>
                <td class="text-muted small">{{ entry.notes || '' }}</td>
              </tr>
            }
            <tr class="row-total">
              <td colspan="2" class="text-end fw-bold small pe-3">Total On-Call Hours:</td>
              <td class="text-center fw-bold">{{ getOncallTotalHours(viewingTimesheet) | number:'1.2-2' }}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    }

    @if (viewingTimesheet.approver_actions?.length) {
      <div class="ts-detail-note" style="background:#f8f9fa;border-color:#dee2e6;color:#212529">
        <div class="fw-semibold mb-2" style="font-size:0.75rem;text-transform:uppercase;letter-spacing:0.06em">Approver History</div>
        @for (act of viewingTimesheet.approver_actions; track act.id) {
          <div class="d-flex gap-2 align-items-start mb-1" style="font-size:0.85rem">
            <span class="material-icons" style="font-size:1rem;margin-top:1px"
              [style.color]="act.action === 'approved' ? '#198754' : '#dc3545'">
              {{ act.action === 'approved' ? 'check_circle' : 'cancel' }}
            </span>
            <span>
              <strong>{{ act.approver_name }}</strong>
              <span style="color:#6c757d"> · {{ act.actioned_at | date:'d MMM yyyy, h:mm a' }}</span>
              <span class="ms-1 fw-semibold"
                [style.color]="act.action === 'approved' ? '#198754' : '#dc3545'">
                {{ act.action === 'approved' ? 'Approved' : 'Rejected' }}
              </span>
              @if (act.reason) {
                <span> — <em>{{ act.reason }}</em></span>
              }
            </span>
          </div>
        }
      </div>
    }

    @if (viewingTimesheet.client_comment) {
      <div class="ts-detail-note">
        <span class="fw-semibold">Comment:</span> {{ viewingTimesheet.client_comment }}
      </div>
    }

    @if (canApprove(viewingTimesheet) || canReject(viewingTimesheet) || (viewingTimesheet.payroll_finalized && viewingTimesheet.status !== 'submitted')) {
      <div class="modal-panel-footer">
        @if (canReject(viewingTimesheet)) {
          <button class="btn btn-sm btn-flag" (click)="closeView(); openReject(viewingTimesheet)">
            <span class="material-icons btn-icon">flag</span> Flag Issue
          </button>
        } @else if (viewingTimesheet.payroll_finalized && viewingTimesheet.status !== 'submitted') {
          <span class="text-muted small fst-italic">Payroll finalized — contact your account manager to report a discrepancy</span>
        }
        @if (canApprove(viewingTimesheet)) {
          <button class="btn btn-sm btn-approve"
            [disabled]="actionPending === viewingTimesheet.id"
            (click)="approve(viewingTimesheet); closeView()"
          >
            @if (actionPending === viewingTimesheet.id) {
              <span class="spinner-border spinner-border-sm"></span>
            } @else {
              <span class="material-icons btn-icon">check_circle</span>
            }
            Approve
          </button>
        }
      </div>
    }

  </div>
}

<!-- Flag / Discrepancy modal -->
@if (rejectingTimesheet) {
  <div class="modal-backdrop-custom" (click)="closeReject()"></div>
  <div class="modal-panel">
    <div class="modal-panel-header">
      <span class="material-icons text-danger me-2">flag</span>
      Flag an Issue
      <button class="modal-close" (click)="closeReject()">
        <span class="material-icons">close</span>
      </button>
    </div>
    <div class="modal-panel-body">
      <p class="text-muted small mb-3">
        Period: <strong>{{ periodLabel(rejectingTimesheet) }}</strong>
      </p>
      <label class="form-label fw-semibold">Describe the issue</label>
      <textarea
        class="form-control"
        rows="4"
        placeholder="e.g. Hours do not match the agreed schedule for this period…"
        [(ngModel)]="rejectReason"
        [class.is-invalid]="rejectError"
      ></textarea>
      @if (rejectError) {
        <div class="invalid-feedback d-block">{{ rejectError }}</div>
      }
    </div>
    <div class="modal-panel-footer">
      <button class="btn btn-outline-secondary btn-sm" (click)="closeReject()">Cancel</button>
      <button class="btn btn-reject-submit btn-sm" [disabled]="rejectSubmitting" (click)="submitReject()">
        @if (rejectSubmitting) {
          <span class="spinner-border spinner-border-sm me-1"></span>
        }
        Submit
      </button>
    </div>
  </div>
}

<!-- Decline leave request modal -->
@if (rejectingLeaveRequest) {
  <div class="modal-backdrop-custom" (click)="closeRejectLeave()"></div>
  <div class="modal-panel">
    <div class="modal-panel-header">
      <span class="material-icons text-danger me-2">close</span>
      {{ rejectingLeaveRequest.status === 'approved' ? 'Rescind Approval' : 'Decline Leave Request' }}
      <button class="modal-close" (click)="closeRejectLeave()">
        <span class="material-icons">close</span>
      </button>
    </div>
    <div class="modal-panel-body">
      <p class="text-muted small mb-3">
        {{ employeeName(rejectingLeaveRequest.employee_id) }} &bull;
        {{ leaveTypeLabel(rejectingLeaveRequest.leave_type) }} &bull;
        {{ leaveDateRange(rejectingLeaveRequest) }}
      </p>
      <label class="form-label fw-semibold">
        {{ rejectingLeaveRequest.status === 'approved' ? 'Reason for rescinding' : 'Reason for declining' }}
      </label>
      <textarea
        class="form-control"
        rows="4"
        placeholder="e.g. No coverage available for this period…"
        [(ngModel)]="rejectLeaveReason"
        [class.is-invalid]="rejectLeaveError"
      ></textarea>
      @if (rejectLeaveError) {
        <div class="invalid-feedback d-block">{{ rejectLeaveError }}</div>
      }
    </div>
    <div class="modal-panel-footer">
      <button class="btn btn-outline-secondary btn-sm" (click)="closeRejectLeave()">Cancel</button>
      <button class="btn btn-reject-submit btn-sm" [disabled]="rejectLeaveSubmitting" (click)="submitRejectLeave()">
        @if (rejectLeaveSubmitting) {
          <span class="spinner-border spinner-border-sm me-1"></span>
        }
        Submit
      </button>
    </div>
  </div>
}
